import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Search, ShieldCheck, AlertTriangle, ArrowRight, Loader2, ExternalLink, Bell, Check } from "lucide-react";
import Seo from "../components/Seo";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const VIN_RE = /^[A-HJ-NPR-Z0-9]{11,17}$/;

/**
 * VinLookupPage — the public "CarFax for batteries" page.
 *
 * Two states based on the route:
 *   /vin            → empty search page with a VIN input
 *   /vin/:vin       → live lookup — either a compact passport summary
 *                     card (with link to full /passport/:id view) or a
 *                     "no signed passport for this VIN" message with a
 *                     CTA to the owner-facing product.
 *
 * By design we expose ONLY the most-recent signed passport per VIN.
 * The owner must generate one to make anything public — the endpoint
 * returns 404-style "found: false" otherwise. This matches the
 * "just the latest signed passport" scope the product chose.
 */
export default function VinLookupPage() {
  const { vin: routeVin } = useParams();
  const navigate = useNavigate();
  const [vin, setVin] = useState(routeVin || "");
  const [state, setState] = useState("idle"); // "idle" | "loading" | "found" | "notfound" | "error"
  const [rec, setRec] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!routeVin) return;
    const v = routeVin.trim().toUpperCase();
    if (!VIN_RE.test(v)) {
      setState("error");
      setErr("That doesn't look like a valid VIN (11–17 letters and digits, no I/O/Q).");
      return;
    }
    setState("loading");
    setErr("");
    fetch(`${API_BASE}/api/vin/${encodeURIComponent(v)}/latest_passport`)
      .then((r) => {
        if (!r.ok) throw new Error("lookup_failed");
        return r.json();
      })
      .then((data) => {
        if (data.found) {
          setRec(data.passport);
          setState("found");
        } else {
          setState("notfound");
        }
      })
      .catch(() => {
        setState("error");
        setErr("Lookup failed — please try again in a moment.");
      });
  }, [routeVin]);

  const onSearch = (e) => {
    e.preventDefault();
    const v = (vin || "").trim().toUpperCase();
    if (!VIN_RE.test(v)) {
      setState("error");
      setErr("Please enter a valid VIN (11–17 letters and digits, no I/O/Q).");
      return;
    }
    setErr("");
    navigate(`/vin/${v}`);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="vin-lookup-page">
      <Seo
        title={routeVin ? `VIN ${routeVin.toUpperCase()} — Tesla Battery Passport lookup` : "Tesla VIN lookup — Battery Passport"}
        description="Look up any Tesla VIN to see its most recent EVIQ Battery Passport — EVIQ Estimated SoH, Trust Score, warranty position, and independently-verified battery history. The CarFax for EV batteries."
        canonical={routeVin ? `https://eviq.pro/vin/${routeVin.toUpperCase()}` : "https://eviq.pro/vin"}
      />
      <div className="max-w-2xl mx-auto px-5 py-10">
        <Link
          to="/"
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1.5 mb-6"
          data-testid="vin-back-home"
        >
          ← EVIQ
        </Link>

        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-2">
            Battery Passport lookup
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-white font-semibold leading-tight">
            The CarFax for <span className="brand-text-gradient">used EV batteries.</span>
          </h1>
          <p className="text-sm text-zinc-400 leading-relaxed mt-3 max-w-lg">
            Shopping a used Tesla? Enter its VIN below. If the current owner has
            generated an EVIQ Battery Passport, you'll see the exact state-of-health,
            EVIQ Score, and warranty headroom — cryptographically signed by EVIQ.
          </p>
        </div>

        {/* Search box */}
        <form onSubmit={onSearch} className="mb-6" data-testid="vin-search-form">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
              VIN (17 characters)
            </span>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                value={vin}
                onChange={(e) => setVin(e.target.value.toUpperCase().replace(/\s+/g, ""))}
                placeholder="5YJ3E1EA7KF000123"
                maxLength={17}
                autoComplete="off"
                spellCheck={false}
                data-testid="vin-input"
                className="flex-1 rounded-lg border border-white/10 bg-black/60 px-3 py-2.5 text-sm text-white placeholder-zinc-600 font-mono tracking-wider focus:outline-none focus:border-[#39FF14]/50 focus:ring-1 focus:ring-[#39FF14]/40"
              />
              <button
                type="submit"
                data-testid="vin-search-submit"
                className="rounded-lg brand-gradient text-black font-semibold px-4 py-2.5 text-sm inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity"
              >
                <Search className="w-4 h-4" strokeWidth={2.4} />
                Look up
              </button>
            </div>
          </label>
        </form>

        {/* Body states */}
        {state === "loading" && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center" data-testid="vin-loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" />
            <div className="mt-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
              Searching passport ledger…
            </div>
          </div>
        )}

        {state === "error" && err && (
          <div
            className="rounded-lg border border-[#FF6B57]/30 bg-[#FF6B57]/[0.08] px-4 py-3 text-sm text-[#FF6B57]"
            data-testid="vin-error"
          >
            {err}
          </div>
        )}

        {state === "notfound" && (
          <NotFoundState vin={routeVin?.toUpperCase() || ""} />
        )}

        {state === "found" && rec && (
          <FoundState rec={rec} />
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
const NotFoundState = ({ vin }) => {
  // Lead-capture: shopper leaves email; we notify them when the current
  // owner publishes a signed passport for this VIN. Every "not found"
  // lookup becomes a funnel entry point.
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // "idle" | "sending" | "done" | "error"
  const [errMsg, setErrMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    const clean = (email || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(clean)) {
      setStatus("error");
      setErrMsg("Please enter a valid email address.");
      return;
    }
    setStatus("sending");
    setErrMsg("");
    try {
      const r = await fetch(`${API_BASE}/api/vin/${encodeURIComponent(vin)}/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: clean }),
      });
      if (!r.ok) throw new Error("subscribe_failed");
      setStatus("done");
    } catch (_e) {
      setStatus("error");
      setErrMsg("Couldn't save your subscription — please try again.");
    }
  };

  return (
    <div
      className="rounded-2xl border p-6"
      style={{ borderColor: "rgba(255,176,32,0.35)", background: "rgba(255,176,32,0.05)" }}
      data-testid="vin-notfound"
    >
      <div className="flex items-start gap-3 mb-3">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#FFB020" }} strokeWidth={2.4} />
        <div className="min-w-0 flex-1">
          <div className="text-white font-semibold text-sm">
            No EVIQ Battery Passport for this VIN
          </div>
          <div className="text-[13px] text-zinc-400 leading-relaxed mt-1">
            The owner of <span className="font-mono text-zinc-200">{vin}</span> hasn't generated
            a signed Battery Passport yet. Without one, we have nothing to publicly verify.
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 pt-4 mt-4 text-[12px] text-zinc-400 leading-relaxed">
        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
          What to do
        </div>
        <ul className="space-y-1.5 pl-3 list-disc marker:text-zinc-600">
          <li>Ask the seller to generate a Battery Passport from the EVIQ app before you buy.</li>
          <li>A signed passport shows SoH, EVIQ Score, warranty headroom, and 3-year projection.</li>
          <li>It's tamper-evident — every passport carries an HMAC-SHA256 signature.</li>
        </ul>
      </div>

      {/* Notify-me lead capture */}
      <div
        className="mt-5 rounded-xl border p-3"
        style={{ borderColor: "rgba(0,229,255,0.28)", background: "rgba(0,229,255,0.04)" }}
        data-testid="vin-notify-block"
      >
        <div className="flex items-center gap-2 mb-2">
          <Bell className="w-3.5 h-3.5" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
          <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "#00E5FF" }}>
            Notify me if a passport is published
          </div>
        </div>
        {status === "done" ? (
          <div className="flex items-start gap-2 text-[12px] text-zinc-200" data-testid="vin-notify-success">
            <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: "#39FF14" }} strokeWidth={2.6} />
            <span>
              You're on the list. We'll email you the moment the current
              owner of this VIN publishes a signed passport.
            </span>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid="vin-notify-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={status === "sending"}
              autoComplete="email"
              maxLength={120}
              data-testid="vin-notify-email"
              className="flex-1 rounded-md border border-white/10 bg-black/60 px-2.5 py-2 text-[12px] text-white placeholder-zinc-600 focus:outline-none focus:border-[#00E5FF]/60 focus:ring-1 focus:ring-[#00E5FF]/40 disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={status === "sending"}
              data-testid="vin-notify-submit"
              className="shrink-0 rounded-md brand-gradient text-black px-3 py-2 text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === "sending" ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.6} /> : <Bell className="w-3 h-3" strokeWidth={2.6} />}
              Notify me
            </button>
          </form>
        )}
        {status === "error" && errMsg && (
          <div className="mt-2 text-[11px] text-[#FF6B57]" data-testid="vin-notify-error">
            {errMsg}
          </div>
        )}
        <div className="mt-2 text-[10px] text-zinc-500 leading-relaxed">
          One email per VIN. Unsubscribe any time. We never share your address.
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-5">
        <Link
          to="/passport/demo"
          data-testid="vin-notfound-see-demo"
          className="rounded-full border border-white/15 px-4 py-2 text-xs text-white inline-flex items-center gap-1.5 hover:bg-white/[0.04]"
        >
          See a demo passport
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
        </Link>
        <a
          href="mailto:?subject=Please%20generate%20an%20EVIQ%20Battery%20Passport&body=Hi%2C%0A%0AI%27m%20interested%20in%20your%20Tesla%20%28VIN%20above%29.%20Could%20you%20please%20connect%20your%20Tesla%20at%20https%3A%2F%2Feviq.pro%20and%20share%20an%20EVIQ%20Battery%20Passport%3F%20It%27s%20a%20signed%20battery%20health%20report%20I%20can%20verify.%0A%0AThanks%21"
          data-testid="vin-notfound-email-seller"
          className="rounded-full brand-gradient text-black px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 hover:opacity-90"
        >
          Email the seller
          <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.4} />
        </a>
      </div>
    </div>
  );
};

const FoundState = ({ rec }) => {
  const s = rec.snapshot || {};
  const score = s.eviq_score;
  const tone =
    score == null    ? "#7FD1FF"
    : score >= 90    ? "#39FF14"
    : score >= 80    ? "#7FD1FF"
    : score >= 70    ? "#00E5FF"
    : score >= 60    ? "#FFB020"
    :                  "#FF6B57";
  const share = `/passport/${rec.passport_id}${rec.signature ? `?sig=${rec.signature}` : ""}`;
  return (
    <div
      className="rounded-2xl border p-6"
      style={{ borderColor: `${tone}55`, background: `linear-gradient(135deg, ${tone}12 0%, rgba(255,255,255,0.02) 100%)` }}
      data-testid="vin-found"
    >
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-4 h-4" style={{ color: tone }} strokeWidth={2.4} />
        <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: tone }}>
          Signed passport found
        </div>
      </div>

      <div className="flex items-baseline gap-6 mb-5">
        {score != null && (
          <div>
            <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">EVIQ Score</div>
            <div className="font-display text-5xl font-semibold leading-none" style={{ color: tone }} data-testid="vin-eviq-score">
              {score}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-wider mt-1" style={{ color: tone }}>
              {s.eviq_score_grade || "—"}
            </div>
          </div>
        )}
        <div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">EVIQ Estimated SoH</div>
          <div className="font-display text-3xl font-semibold leading-none text-white mt-1" data-testid="vin-soh">
            {s.soh_pct}%
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-[12px] mb-5">
        <div>
          <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">Vehicle</div>
          <div className="text-white mt-0.5">{s.year} {s.model}{s.trim ? ` · ${s.trim}` : ""}</div>
        </div>
        <div>
          <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">Warranty room</div>
          <div className="text-white mt-0.5">
            {(s.soh_pct - (s.warranty_threshold_pct || 70)).toFixed(1)} pts above floor
          </div>
        </div>
        <div>
          <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">Degradation trend</div>
          <div className="text-white mt-0.5">
            {Math.abs(s.delta_30d_pct).toFixed(2)}% / month
          </div>
        </div>
        <div>
          <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">3-year forecast</div>
          <div className="text-white mt-0.5">
            {s.forecast_3yr_soh != null ? `${s.forecast_3yr_soh}%` : "—"}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 pt-4 flex items-center justify-between gap-3">
        <div className="font-mono text-[10px] text-zinc-500 leading-snug">
          Passport <span className="text-zinc-300">{rec.passport_id}</span>
          <span className="mx-1">·</span>
          issued {new Date(rec.created_at).toLocaleDateString()}
        </div>
        <Link
          to={share}
          data-testid="vin-open-passport"
          className="shrink-0 rounded-full brand-gradient text-black px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 hover:opacity-90"
        >
          Open full passport
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
        </Link>
      </div>
    </div>
  );
};
